import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';
import { UserService } from './user.service';
import { AuthService } from './auth.service';
import { loginApi } from '../../config';
import { CommonRouteService } from './common-route.service';
import { finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { FooterService } from './footer.service';
import { PKCEUtil } from '../auth/pkce.util';

@Injectable({
  providedIn: 'root',
})
export class OidcService {
  constructor(
    readonly router: Router,
    readonly userService: UserService,
    readonly authService: AuthService,
    readonly commonRouteService: CommonRouteService,
    readonly footerService: FooterService
  ) {}

  autoLogin(): void {
    console.info('🎉 SR autoLogin triggered.');

    const accessToken = this.authService.accessToken;
    const accessTokenExpiry = Number(this.authService.accessTokenExpiry);
    const currentTime = Math.floor(Date.now() / 1000);
    const pathname = window.location.pathname;

    if (accessToken && currentTime < accessTokenExpiry) {
      if (!this.authService.userProfile) {
        console.log('❌ user profile not found. ');
        this.forceLogin();
        return;
      }

      if (!pathname || pathname === '/' || pathname === '') {
        this.commonRouteService.navigateUserAfterLogin(
          this.authService.userProfile
        );
      }

      return;
    }

    const params = new URLSearchParams(window.location.search);

    if (params.has('logout')) {
      return;
    }

    if (params.has('code')) {
      return;
    }

    this.loginRedirect();
  }

  async loginRedirect(): Promise<void> {
    console.info('🎉 SR loginRedirect triggered.');
    localStorage.clear();
    sessionStorage.clear();

    document.cookie.split(';').forEach((c) => {
      document.cookie = c
        .replace(/^ +/, '')
        .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
    });

    const returnUrl = window.location.pathname;
    this.authService.returnUrl = returnUrl;

    const verifier = await PKCEUtil.generateCodeVerifier();
    const challenge = await PKCEUtil.generateCodeChallenge(verifier);
    const state =
      Math.random().toString(36).substring(2) + Date.now().toString();
    this.authService.codeVerifier = verifier;
    this.authService.oidcState = state;

    const params = new HttpParams()
      .set('client_id', environment.oidc.clientId)
      .set('redirect_uri', environment.oidc.redirectUri)
      .set('response_type', 'code')
      .set('scope', environment.oidc.scopes.join(' '))
      .set('code_challenge', challenge)
      .set('code_challenge_method', 'S256')
      .set('state', state)
      .set('prompt', 'login');

    const redirectUrl = loginApi.authorizationUrl + '?' + params.toString();
    window.location.href = redirectUrl;
  }

  forceLogin(): void {
    console.log('Triggering forceLogin....');
    this.authService.clearAll();
    window.location.href = '/';
  }

  getUserFlags(userData: any): { isUserVerified: boolean } {
    const userObj = { isUserVerified: true };
    if ('userEnrolled' in userData && !userData.userEnrolled) {
      userObj.isUserVerified = false;
    }
    if (
      userData.companyBlocked ||
      (userData.companyEligibleChallengesDTO &&
        userData.companyEligibleChallengesDTO.companyBlocked)
    ) {
      userObj.isUserVerified = false;
    }
    if (
      userData.userType == 'PAM' &&
      !userData.pamDefaultSelection.beGeoIds
    ) {
      userObj.isUserVerified = false;
    }
    if (
      this.router.url.includes('sca') ||
      this.router.url.includes('email') ||
      this.router.url.includes('role') ||
      this.router.url.includes('emails')
    ) {
      userObj.isUserVerified = false;
    }
    return userObj;
  }

  async handleCallback(): Promise<void> {
    console.info('🎉 SR Callback triggered.');
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (!code) {
      console.log('❌ Invalid code.  code', code);
      this.forceLogin();
      return;
    }

    const returnedState = params.get('state');
    const storedState = this.authService.oidcState;

    if (!returnedState) {
      console.log(
        '❌ returnedState Invalid state.  returnedState',
        returnedState
      );
      this.forceLogin();
      return;
    }

    if (!storedState) {
      console.log('❌ storedState Invalid state.  storedState', storedState);
      this.forceLogin();
      return;
    }

    if (returnedState !== storedState) {
      console.log('❌ Invalid state. not same. ');
      this.forceLogin();
      return;
    }

    const codeVerifier = this.authService.codeVerifier;
    if (!codeVerifier) {
      console.log('❌ Invalid code verifier. ', codeVerifier);
      this.forceLogin();
      return;
    }

    const body = {
      client_id: environment.oidc.clientId,
      grant_type: environment.oidc.grant_type,
      redirect_uri: environment.oidc.redirectUri,
      code: code,
      code_verifier: codeVerifier,
    };

    this.userService.getToken(body).then(
      (response) => {
        console.log('getToken res', response);

        const tokens = response;

        if (!tokens.access_token || !tokens.refresh_token) {
          console.error('❌ Missing tokens. ');
          this.forceLogin();
          return;
        }

        this.authService.storeTokens(tokens);

        this.authService.flushCodeVerifier();
        this.authService.flushOidcState();

        const newUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        this.userService
          .attemptAuth()
          .pipe(finalize(() => {}))
          .subscribe(
            (data: any) => {
              if (!data) {
                console.log('❌ User not found. ');
                this.forceLogin();
                return;
              }

              this.authService.userProfile = {
                ...data,
                ...this.getUserFlags(data),
              };

              const returnUrl = this.authService.returnUrl;

              if (!returnUrl || returnUrl === '/' || returnUrl === '') {
                this.commonRouteService.navigateUserAfterLogin(
                  this.authService.userProfile
                );
              } else {
                this.authService.flushReturnUrl();
                this.commonRouteService.setupUserData(
                  this.authService.userProfile
                ).then((alreadyNavigated) => {
                  if (!alreadyNavigated) {
                    this.router.navigateByUrl(returnUrl);
                  }
                });
              }
            },
            (error: any) => {
              console.error('❌ Authentication failed:', error);
              this.forceLogin();
            }
          );
      },
      (error: any) => {
        console.error('❌ get token Error:', error);
        this.forceLogin();
      }
    );
  }

  refreshAccessToken(): void {
    const refreshToken = this.authService.refreshToken;
    const refreshTokenExpiry = Number(this.authService.refershTokenExpiry);
    const currentTime = Math.floor(Date.now() / 1000);

    if (
      !refreshToken ||
      !refreshTokenExpiry ||
      currentTime >= refreshTokenExpiry
    ) {
      return;
    }

    const body = {
      client_id: environment.oidc.clientId,
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    };
    this.userService.getToken(body).then(
      (response) => {
        const tokens = response;

        if (!tokens.access_token || !tokens.refresh_token) {
          console.error('❌ Missing tokens. ');
          this.forceLogin();
          return;
        }

        this.authService.storeTokens(tokens);
      },
      (error: any) => {
        console.error('❌ Error:', error);
        this.forceLogin();
      }
    );
  }
}
